import React, { useState, useRef, useEffect } from 'react';
import { Upload, Download, FileText, Users, Package, Activity, Plus, Edit, Trash2, BarChart2, Eye, MapPin, Clock, Database, Check } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../App';
import * as Papa from 'papaparse';
import { v4 as uuidv4 } from 'uuid';
import BulkUpload from './BulkUpload';

interface CSVRow {
  ITMREF: string;
  LOC: string;
  QTYPCU: string;
  QTYSTU: string;
  [key: string]: string;
}

const AdminDashboard: React.FC = () => {
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [uploading, setUploading] = useState(false);
  const [sessions, setSessions] = useState<any[]>([]);

  const [activeTab, setActiveTab] = useState('dashboard');
  const [products, setProducts] = useState<any[]>([]);
  const [showProductModal, setShowProductModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState<any>(null);
  const [productForm, setProductForm] = useState({
    item_ref: '',
    name: '',
    description: '',
    unit: 'EA'
  });
  const [showBarcodeModal, setShowBarcodeModal] = useState(false);
  const [currentProductBarcodes, setCurrentProductBarcodes] = useState<any[]>([]);
  const [newBarcode, setNewBarcode] = useState('');
  const [showSessionDetailsModal, setShowSessionDetailsModal] = useState(false);
  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [sessionItems, setSessionItems] = useState<any[]>([]);
  const [loadingSessionItems, setLoadingSessionItems] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Reporting state
  const [reportingData, setReportingData] = useState<any>(null);
  const [loadingReports, setLoadingReports] = useState(false);
  const [reportStartDate, setReportStartDate] = useState('');
  const [reportEndDate, setReportEndDate] = useState('');
  
  // Session renaming state
  const [editingSessionId, setEditingSessionId] = useState<string | null>(null);
  const [editingSessionName, setEditingSessionName] = useState('');

  useEffect(() => {
    fetchSessions();
    if (activeTab === 'products') {
      fetchProducts();
    }
    if (activeTab === 'reporting') {
      // Set default date range to last 30 days
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      setReportEndDate(endDate.toISOString().split('T')[0]);
      setReportStartDate(startDate.toISOString().split('T')[0]);
    }
  }, [activeTab]);

  const fetchSessions = async () => {
    try {
      const { data, error } = await supabase
        .from('count_sessions')
        .select(`
          *,
          count_items (
            id,
            status
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;

      const formattedSessions = data.map(session => ({
        ...session,
        items_count: session.count_items.length,
        completed_count: session.count_items.filter((item: any) => item.status === 'completed').length
      }));

      setSessions(formattedSessions);
    } catch (error) {
      console.error('Error fetching sessions:', error);
    }
  };

  const updateSessionStatus = async (sessionId: string, newStatus: string) => {
    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ status: newStatus })
        .eq('id', sessionId);

      if (error) throw error;
      
      // Refresh sessions list
      fetchSessions();
    } catch (error) {
      console.error('Error updating session status:', error);
      alert('Error updating session status');
    }
  };

  const handleDeleteSession = async (sessionId: string, sessionName: string) => {
    if (!window.confirm(`Are you sure you want to delete the count session "${sessionName}"? This will also delete all associated count items and cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('count_sessions')
        .delete()
        .eq('id', sessionId);

      if (error) throw error;
      
      // Refresh sessions list
      fetchSessions();
      alert('Count session deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting session:', error);
      alert(`Error deleting session: ${error.message}`);
    }
  };

  const handleStartEditingSession = (sessionId: string, currentName: string) => {
    setEditingSessionId(sessionId);
    setEditingSessionName(currentName);
  };

  const handleCancelEditingSession = () => {
    setEditingSessionId(null);
    setEditingSessionName('');
  };

  const handleSaveSessionName = async () => {
    if (!editingSessionId || !editingSessionName.trim()) return;

    try {
      const { error } = await supabase
        .from('count_sessions')
        .update({ 
          name: editingSessionName.trim(),
          updated_at: new Date().toISOString()
        })
        .eq('id', editingSessionId);

      if (error) throw error;

      // Reset editing state
      setEditingSessionId(null);
      setEditingSessionName('');
      
      // Refresh sessions list
      fetchSessions();
      
    } catch (error: any) {
      console.error('Error updating session name:', error);
      alert(`Error updating session name: ${error.message}`);
    }
  };

  const handleViewSessionDetails = async (session: any) => {
    setSelectedSession(session);
    setShowSessionDetailsModal(true);
    setLoadingSessionItems(true);
    
    try {
      const { data, error } = await supabase
        .from('count_items')
        .select(`
          *,
          products (
            item_ref,
            name,
            unit
          )
        `)
        .eq('session_id', session.id)
        .order('csv_line_number', { ascending: true });

      if (error) throw error;
      setSessionItems(data || []);
    } catch (error: any) {
      console.error('Error fetching session items:', error);
      alert(`Error loading session details: ${error.message}`);
    } finally {
      setLoadingSessionItems(false);
    }
  };



  const fetchProducts = async () => {
    try {
      const { data, error } = await supabase
        .from('products')
        .select(`
          *,
          barcodes (
            id,
            barcode
          )
        `)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    }
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setProductForm({
      item_ref: '',
      name: '',
      description: '',
      unit: 'EA'
    });
    setShowProductModal(true);
  };

  const handleEditProduct = (product: any) => {
    setEditingProduct(product);
    setProductForm({
      item_ref: product.item_ref,
      name: product.name,
      description: product.description || '',
      unit: product.unit
    });
    setShowProductModal(true);
  };

  const handleSaveProduct = async () => {
    if (!productForm.item_ref || !productForm.name) return;

    try {
      if (editingProduct) {
        // Update existing product
        const { error } = await supabase
          .from('products')
          .update({
            name: productForm.name,
            description: productForm.description,
            unit: productForm.unit
          })
          .eq('id', editingProduct.id);

        if (error) throw error;
      } else {
        // Create new product
        const { error } = await supabase
          .from('products')
          .insert({
            item_ref: productForm.item_ref,
            name: productForm.name,
            description: productForm.description,
            unit: productForm.unit
          });

        if (error) throw error;
      }

      setShowProductModal(false);
      fetchProducts();
      alert('Product saved successfully!');
    } catch (error: any) {
      console.error('Error saving product:', error);
      alert(`Error saving product: ${error.message}`);
    }
  };

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product? This will also delete all associated barcodes.')) {
      return;
    }

    try {
      const { error } = await supabase
        .from('products')
        .delete()
        .eq('id', productId);

      if (error) throw error;
      
      fetchProducts();
      alert('Product deleted successfully!');
    } catch (error: any) {
      console.error('Error deleting product:', error);
      alert(`Error deleting product: ${error.message}`);
    }
  };

  const handleManageBarcodes = async (product: any) => {
    setEditingProduct(product);
    setCurrentProductBarcodes(product.barcodes || []);
    setNewBarcode('');
    setShowBarcodeModal(true);
  };

  const handleAddBarcode = async () => {
    if (!newBarcode.trim() || !editingProduct) return;

    try {
      const { error } = await supabase
        .from('barcodes')
        .insert({
          product_id: editingProduct.id,
          barcode: newBarcode.trim()
        });

      if (error) throw error;

      // Refresh barcodes
      const { data: updatedBarcodes } = await supabase
        .from('barcodes')
        .select('*')
        .eq('product_id', editingProduct.id);

      setCurrentProductBarcodes(updatedBarcodes || []);
      setNewBarcode('');
      fetchProducts(); // Refresh main products list
    } catch (error: any) {
      console.error('Error adding barcode:', error);
      alert(`Error adding barcode: ${error.message}`);
    }
  };

  const handleDeleteBarcode = async (barcodeId: string) => {
    try {
      const { error } = await supabase
        .from('barcodes')
        .delete()
        .eq('id', barcodeId);

      if (error) throw error;

      // Refresh barcodes
      const { data: updatedBarcodes } = await supabase
        .from('barcodes')
        .select('*')
        .eq('product_id', editingProduct?.id);

      setCurrentProductBarcodes(updatedBarcodes || []);
      fetchProducts(); // Refresh main products list
    } catch (error: any) {
      console.error('Error deleting barcode:', error);
      alert(`Error deleting barcode: ${error.message}`);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const uploadedFile = event.target.files?.[0];
    if (uploadedFile) {
      setFile(uploadedFile);
    }
  };

  const processCSV = async () => {
    if (!file || !user) return;

    setUploading(true);
    try {
      const text = await file.text();
      const result = Papa.parse(text, {
        header: false,
        skipEmptyLines: true,
        delimiter: ';'
      });

      const data = result.data as string[][];
      
      // Find the header row (contains "ITMREF", "LOC", etc.)
      const headerRowIndex = data.findIndex(row => 
        row.some(cell => cell && cell.includes('ITMREF'))
      );
      
      if (headerRowIndex === -1) {
        throw new Error('Could not find header row in CSV');
      }

      const headers = data[headerRowIndex];
      
      // More robust filtering to exclude header rows
      const dataRows = data.slice(headerRowIndex + 1).filter(row => {
        // Filter out empty rows
        if (row.length === 0 || !row.some(cell => cell && cell.trim() !== '')) {
          return false;
        }
        
        // Filter out any row that contains header-like content
        const rowText = row.join('').toLowerCase();
        const headerIndicators = [
          'itmref', 'product', 'status', 'container number', 'location', 'lot', 
          'unit', 'stock pac', 'counted stock', 'variance', 'theoretical val'
        ];
        
        // If any cell contains header indicators, exclude this row
        const hasHeaderContent = row.some(cell => {
          if (!cell) return false;
          const cellText = cell.toString().toLowerCase().trim();
          return headerIndicators.some(indicator => cellText.includes(indicator));
        });
        
        if (hasHeaderContent) {
          console.log(`Filtering out header-like row: ${row.slice(0, 3).join(';')}`);
          return false;
        }
        
        return true;
      });

      // Extract metadata from the CSV
      const sessionName = `Stock Count ${new Date().toLocaleDateString()}`;
      const site = data.find(row => row.some(cell => cell.includes('STOFCY')))?.[2] || 'Unknown';
      
      // Extract the original worksheet ID (CUNLISNUM)
      const originalWorksheetId = data.find(row => row.some(cell => cell.includes('CUNLISNUM')))?.[2] || null;

      // Create count session
      const { data: session, error: sessionError } = await supabase
        .from('count_sessions')
        .insert({
          name: sessionName,
          description: `Imported from ${file.name}${originalWorksheetId ? ' - Worksheet: ' + originalWorksheetId : ''}`,
          site: site,
          status: 'pending',
          created_by: user.id
        })
        .select()
        .single();

      if (sessionError) throw sessionError;

      console.log(`Processing ${dataRows.length} data rows from CSV`);

      // Process each data row
      const countItems = [];
      const newProducts = [];
      const processedProducts = new Map(); // Cache to avoid duplicate checks

      // Create or find the special empty location product
      let emptyLocationProductId = null;
      const { data: emptyLocationProduct } = await supabase
        .from('products')
        .select('id')
        .eq('item_ref', 'EMPTY_LOCATION')
        .maybeSingle();

      if (emptyLocationProduct) {
        emptyLocationProductId = emptyLocationProduct.id;
      } else {
        // Create the empty location product
        const newEmptyProductId = uuidv4();
        const emptyProduct = {
          id: newEmptyProductId,
          item_ref: 'EMPTY_LOCATION',
          name: 'Empty Location (No Products Expected)',
          unit: 'EA'
        };
        newProducts.push(emptyProduct);
        emptyLocationProductId = newEmptyProductId;
        processedProducts.set('EMPTY_LOCATION', newEmptyProductId);
      }

      for (let i = 0; i < dataRows.length; i++) {
        const row = dataRows[i];
        const csvLineNumber = headerRowIndex + 2 + i; // +2 because we skip header and array is 0-indexed, but CSV lines are 1-indexed
        const itemRef = row[headers.indexOf('ITMREF')]?.trim();
        const location = row[headers.indexOf('LOC')]?.trim();
        const quantityStr = row[headers.indexOf('QTYPCU')]?.trim() || '0';
        const quantity = parseInt(quantityStr) || 0; // Default to 0 if parsing fails
        
        // Skip if no location (location is always required)
        if (!location || location === '') continue;
        
        // Handle empty locations (location exists but no itemRef)
        if (!itemRef || itemRef === '') {
          // This is an empty location - add it with the empty location product
          countItems.push({
            session_id: session.id,
            product_id: emptyLocationProductId,
            location: location,
            expected_quantity: 0, // Empty locations expect 0 items
            status: 'pending',
            csv_line_number: csvLineNumber,
            original_worksheet_id: originalWorksheetId
          });
          continue;
        }
        
        // Additional validation - skip if itemRef looks like a header value
        const invalidItemRefs = ['product', 'status', 'itmref', 'container number', 'location', 'lot', 'unit'];
        if (invalidItemRefs.some(invalid => itemRef.toLowerCase().includes(invalid))) {
          console.log(`Skipping invalid itemRef that looks like header: ${itemRef}`);
          continue;
        }

        let productId;
        
        // Check if we've already processed this product in this upload
        if (processedProducts.has(itemRef)) {
          productId = processedProducts.get(itemRef);
        } else {
          // Check if product exists in database
          const { data: existingProduct } = await supabase
            .from('products')
            .select('id')
            .eq('item_ref', itemRef)
            .maybeSingle();

          if (existingProduct) {
            productId = existingProduct.id;
            processedProducts.set(itemRef, productId);
          } else {
            // Create new product
            const newProductId = uuidv4();
            const newProduct = {
              id: newProductId,
              item_ref: itemRef,
              name: itemRef, // Use item_ref as name for now
              unit: 'EA'
            };
            newProducts.push(newProduct);
            productId = newProductId;
            processedProducts.set(itemRef, productId);
          }
        }

        // Validate data before adding to count items
        if (productId && location && !isNaN(quantity)) {
          countItems.push({
            session_id: session.id,
            product_id: productId,
            location: location,
            expected_quantity: quantity,
            status: 'pending',
            csv_line_number: csvLineNumber,
            original_worksheet_id: originalWorksheetId
          });
        } else {
          console.warn(`Skipping invalid row: itemRef=${itemRef}, location=${location}, quantity=${quantity}`);
        }
      }

      // Insert new products (only ones that don't exist)
      if (newProducts.length > 0) {
        try {
          // Try to insert new products, ignore duplicates
          const { error: productsError } = await supabase
            .from('products')
            .insert(newProducts)
            .select();
          
          // If we get a duplicate key error, that's okay - the products already exist
          if (productsError && !productsError.message.includes('duplicate key value')) {
            throw productsError;
          }
        } catch (error: any) {
          // If it's not a duplicate error, throw it
          if (!error.message.includes('duplicate key value')) {
            throw error;
          }
          // Otherwise, silently continue - duplicates are expected
        }
      }

      // Insert count items
      if (countItems.length > 0) {
        const { error: itemsError } = await supabase
          .from('count_items')
          .insert(countItems);
        if (itemsError) throw itemsError;
        
        console.log(`Successfully inserted ${countItems.length} count items`);
      } else {
        throw new Error('No valid count items found in CSV. Please check the file format.');
      }

      // Reset form
      setFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      
      // Refresh sessions list
      fetchSessions();
      
      alert(`Successfully imported ${countItems.length} items to session "${session.name}"`);
      console.log(`CSV import completed successfully`);
      
    } catch (error: any) {
      console.error('Error processing CSV:', error);
      alert(`Error processing CSV: ${error.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Reporting functions
  const fetchReportingData = async () => {
    if (!reportStartDate || !reportEndDate) return;
    
    setLoadingReports(true);
    try {
      // Get all completed count items within the date range
      const { data: countItems, error } = await supabase
        .from('count_items')
        .select(`
          id,
          expected_quantity,
          actual_quantity,
          counted_at,
          products (
            item_ref,
            name
          ),
          count_sessions (
            name,
            site
          )
        `)
        .eq('status', 'completed')
        .gte('counted_at', reportStartDate + 'T00:00:00Z')
        .lte('counted_at', reportEndDate + 'T23:59:59Z')
        .not('actual_quantity', 'is', null);

      if (error) throw error;

      if (!countItems || countItems.length === 0) {
        setReportingData({
          totalItems: 0,
          correctItems: 0,
          correctPercentage: 0,
          averageDiscrepancy: 0,
          dailyData: []
        });
        return;
      }

      // Calculate metrics
      const totalItems = countItems.length;
      const correctItems = countItems.filter(item => 
        item.actual_quantity === item.expected_quantity
      ).length;
      const correctPercentage = (correctItems / totalItems) * 100;

      // Calculate average discrepancy
      let totalDiscrepancy = 0;
      let validDiscrepancyItems = 0;

      countItems.forEach(item => {
        const expected = item.expected_quantity;
        const actual = item.actual_quantity;
        
        if (expected === 0 && actual === 0) {
          // Both zero, perfect match, 0% discrepancy
          validDiscrepancyItems++;
        } else if (expected === 0 && actual > 0) {
          // Expected zero but found items - cap at 100% discrepancy
          totalDiscrepancy += 100;
          validDiscrepancyItems++;
        } else if (expected > 0) {
          // Normal case - calculate percentage difference
          const discrepancy = Math.abs(actual - expected) / expected * 100;
          // Cap discrepancy at 100% to avoid extreme outliers
          totalDiscrepancy += Math.min(discrepancy, 100);
          validDiscrepancyItems++;
        }
      });

      const averageDiscrepancy = validDiscrepancyItems > 0 ? totalDiscrepancy / validDiscrepancyItems : 0;

      // Group by day for trend analysis
      const dailyData = countItems.reduce((acc: any[], item) => {
        const date = new Date(item.counted_at).toISOString().split('T')[0];
        const existingDay = acc.find(d => d.date === date);
        
        if (existingDay) {
          existingDay.totalItems++;
          if (item.actual_quantity === item.expected_quantity) {
            existingDay.correctItems++;
          }
          
          // Add to discrepancy calculation
          const expected = item.expected_quantity;
          const actual = item.actual_quantity;
          
          if (expected === 0 && actual === 0) {
            existingDay.validDiscrepancyItems++;
          } else if (expected === 0 && actual > 0) {
            existingDay.totalDiscrepancy += 100;
            existingDay.validDiscrepancyItems++;
          } else if (expected > 0) {
            const discrepancy = Math.abs(actual - expected) / expected * 100;
            existingDay.totalDiscrepancy += Math.min(discrepancy, 100);
            existingDay.validDiscrepancyItems++;
          }
        } else {
          const expected = item.expected_quantity;
          const actual = item.actual_quantity;
          let dayDiscrepancy = 0;
          let dayValidItems = 0;
          
          if (expected === 0 && actual === 0) {
            dayValidItems = 1;
          } else if (expected === 0 && actual > 0) {
            dayDiscrepancy = 100;
            dayValidItems = 1;
          } else if (expected > 0) {
            dayDiscrepancy = Math.abs(actual - expected) / expected * 100;
            dayDiscrepancy = Math.min(dayDiscrepancy, 100);
            dayValidItems = 1;
          }
          
          acc.push({
            date,
            totalItems: 1,
            correctItems: item.actual_quantity === item.expected_quantity ? 1 : 0,
            totalDiscrepancy: dayDiscrepancy,
            validDiscrepancyItems: dayValidItems
          });
        }
        return acc;
      }, []);

      // Calculate percentages for daily data
      dailyData.forEach(day => {
        day.correctPercentage = (day.correctItems / day.totalItems) * 100;
        day.averageDiscrepancy = day.validDiscrepancyItems > 0 ? 
          day.totalDiscrepancy / day.validDiscrepancyItems : 0;
      });

      // Sort by date
      dailyData.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

      setReportingData({
        totalItems,
        correctItems,
        correctPercentage,
        averageDiscrepancy,
        dailyData
      });

    } catch (error) {
      console.error('Error fetching reporting data:', error);
      alert('Error loading reporting data');
    } finally {
      setLoadingReports(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">Admin Dashboard</h1>
        <div className="flex gap-2">
          {activeTab === 'products' && (
            <button 
              className="btn btn-primary"
              onClick={handleAddProduct}
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Product
            </button>
          )}
          <button className="btn btn-outline">
            <Download className="w-4 h-4 mr-2" />
            Export Results
          </button>
        </div>
      </div>

      {/* Navigation Tabs */}
      <div className="tabs tabs-boxed tabs-lg bg-base-200 p-1 mb-6 shadow-sm">
        <button 
          className={`tab gap-2 font-semibold transition-all duration-200 ${
            activeTab === 'dashboard' 
              ? 'tab-active bg-primary text-primary-content shadow-sm' 
              : 'hover:bg-base-300 text-base-content'
          }`}
          onClick={() => setActiveTab('dashboard')}
        >
          <BarChart2 className="w-4 h-4" />
          Dashboard
        </button>
        <button 
          className={`tab gap-2 font-semibold transition-all duration-200 ${
            activeTab === 'products' 
              ? 'tab-active bg-primary text-primary-content shadow-sm' 
              : 'hover:bg-base-300 text-base-content'
          }`}
          onClick={() => setActiveTab('products')}
        >
          <Package className="w-4 h-4" />
          Products
        </button>
        <button 
          className={`tab gap-2 font-semibold transition-all duration-200 ${
            activeTab === 'reporting' 
              ? 'tab-active bg-primary text-primary-content shadow-sm' 
              : 'hover:bg-base-300 text-base-content'
          }`}
          onClick={() => setActiveTab('reporting')}
        >
          <BarChart2 className="w-4 h-4" />
          Reporting
        </button>
      </div>

      {/* Dashboard Tab Content */}
      {activeTab === 'dashboard' && (
        <>
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Active Sessions</div>
          <div className="stat-value text-purple-600">
            {sessions.filter(s => s.status === 'active').length}
          </div>
          <div className="stat-desc">Currently running</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Total Sessions</div>
          <div className="stat-value text-purple-600">{sessions.length}</div>
          <div className="stat-desc">All time</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Total Items</div>
          <div className="stat-value text-purple-600">
            {sessions.reduce((acc, session) => acc + session.items_count, 0)}
          </div>
          <div className="stat-desc">To be counted</div>
        </div>
        <div className="stat bg-base-100 rounded-lg shadow">
          <div className="stat-title">Completed</div>
          <div className="stat-value text-purple-600">
            {sessions.filter(s => s.status === 'completed' || s.status === 'reopened').length}
          </div>
          <div className="stat-desc">Sessions done</div>
        </div>
      </div>

      {/* CSV Upload */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">
            <Upload className="w-5 h-5" />
            Upload Count Schedule
          </h2>
          <p className="text-base-content/70">
            Upload a CSV file to create a new count session. The file should contain product codes, locations, and expected quantities.
          </p>
          
          <div className="form-control w-full max-w-xs">
            <label className="label">
              <span className="label-text">Choose CSV file</span>
            </label>
            <input
              ref={fileInputRef}
              type="file"
              accept=".csv"
              className="file-input file-input-bordered w-full max-w-xs"
              onChange={handleFileUpload}
            />
          </div>

          {file && (
            <div className="mt-4 p-4 bg-base-200 rounded-lg">
              <p className="font-medium">Selected file: {file.name}</p>
              <p className="text-sm text-base-content/70">Size: {(file.size / 1024).toFixed(1)} KB</p>
            </div>
          )}

          <div className="card-actions justify-end">
            <button
              className={`btn btn-primary ${uploading ? 'loading' : ''}`}
              onClick={processCSV}
              disabled={!file || uploading}
            >
              {uploading ? 'Processing...' : 'Process CSV'}
            </button>
          </div>
        </div>
      </div>

      {/* Sessions Table */}
      <div className="card bg-base-100 shadow-xl">
        <div className="card-body">
          <h2 className="card-title">Count Sessions</h2>
          <div className="overflow-x-auto">
            <table className="table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Site</th>
                  <th>Status</th>
                  <th>Progress</th>
                  <th>Created</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {sessions.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center text-base-content/70">
                      No count sessions found. Upload a CSV to create your first session.
                    </td>
                  </tr>
                ) : (
                  sessions.map((session) => (
                    <tr key={session.id}>
                      <td>
                        {editingSessionId === session.id ? (
                          <div className="flex gap-2 items-center">
                            <input
                              type="text"
                              className="input input-bordered input-sm flex-1"
                              value={editingSessionName}
                              onChange={(e) => setEditingSessionName(e.target.value)}
                              onKeyPress={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveSessionName();
                                } else if (e.key === 'Escape') {
                                  handleCancelEditingSession();
                                }
                              }}
                              autoFocus
                            />
                            <button
                              className="btn btn-sm btn-success"
                              onClick={handleSaveSessionName}
                              disabled={!editingSessionName.trim()}
                            >
                              <Check className="w-3 h-3" />
                            </button>
                            <button
                              className="btn btn-sm btn-ghost"
                              onClick={handleCancelEditingSession}
                            >
                              ✕
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <span className="flex-1">{session.name}</span>
                            <button
                              className="btn btn-ghost btn-xs opacity-50 hover:opacity-100"
                              onClick={() => handleStartEditingSession(session.id, session.name)}
                              title="Rename session"
                            >
                              <Edit className="w-3 h-3" />
                            </button>
                          </div>
                        )}
                      </td>
                      <td>{session.site}</td>
                      <td>
                        <span className={`badge ${
                          session.status === 'completed' ? 'badge-success' :
                          session.status === 'reopened' ? 'badge-info' :
                          session.status === 'active' ? 'badge-warning' :
                          'badge-ghost'
                        }`}>
                          {session.status}
                        </span>
                      </td>
                      <td>
                        <div className="w-32">
                          <div className="flex justify-between text-xs mb-1">
                            <span>{session.completed_count}</span>
                            <span>{session.items_count}</span>
                          </div>
                          <progress 
                            className="progress progress-primary w-full h-2" 
                            value={session.completed_count} 
                            max={session.items_count}
                          ></progress>
                          <div className="text-xs text-base-content/70 mt-1">
                            {session.items_count > 0 ? Math.round((session.completed_count / session.items_count) * 100) : 0}%
                          </div>
                        </div>
                      </td>
                      <td>{new Date(session.created_at).toLocaleDateString()}</td>
                      <td>
                        <div className="flex gap-2">
                          <button 
                            className="btn btn-sm btn-outline"
                            onClick={() => handleViewSessionDetails(session)}
                          >
                            <Eye className="w-4 h-4 mr-1" />
                            View Details
                          </button>
                          {session.status === 'pending' && (
                            <button 
                              className="btn btn-sm btn-primary"
                              onClick={() => updateSessionStatus(session.id, 'active')}
                            >
                              Start
                            </button>
                          )}
                          {session.status === 'active' && (
                            <button 
                              className="btn btn-sm btn-warning"
                              onClick={() => updateSessionStatus(session.id, 'pending')}
                            >
                              Pause
                            </button>
                          )}
                          {session.status === 'completed' && (
                            <button 
                              className="btn btn-sm btn-ghost"
                              onClick={() => updateSessionStatus(session.id, 'active')}
                            >
                              Reopen
                            </button>
                          )}
                          <button 
                            className="btn btn-sm btn-error"
                            onClick={() => handleDeleteSession(session.id, session.name)}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
        </>
      )}

      {/* Products Tab Content */}
      {activeTab === 'products' && (
        <div className="space-y-6">
          {/* Bulk Upload Section */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">
                <Database className="w-5 h-5" />
                Bulk Upload Master Data
              </h2>
              <p className="text-base-content/70 mb-4">
                Upload a CSV file to create or update products in bulk. Use this to import your master product catalog.
              </p>
              <BulkUpload onUploadComplete={fetchProducts} />
            </div>
          </div>

          {/* Products Management Section */}
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Products Management</h2>
              
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>Item Reference</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Unit</th>
                      <th>Barcodes</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {products.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-center text-base-content/70">
                          No products found. Add your first product or upload a CSV to get started.
                        </td>
                      </tr>
                    ) : (
                      products.map((product) => (
                        <tr key={product.id}>
                          <td className="font-mono text-sm">{product.item_ref}</td>
                          <td className="font-medium">{product.name}</td>
                          <td className="text-sm text-base-content/70">
                            {product.description || '-'}
                          </td>
                          <td>
                            <span className="badge badge-outline">{product.unit}</span>
                          </td>
                          <td>
                            <div className="flex flex-wrap gap-1">
                              {product.barcodes?.length > 0 ? (
                                product.barcodes.map((barcode: any) => (
                                  <span key={barcode.id} className="badge badge-secondary badge-sm">
                                    {barcode.barcode}
                                  </span>
                                ))
                              ) : (
                                <span className="text-sm text-base-content/50">No barcodes</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-2">
                              <button
                                className="btn btn-sm btn-outline"
                                onClick={() => handleEditProduct(product)}
                              >
                                <Edit className="w-4 h-4" />
                              </button>
                              <button
                                className="btn btn-sm btn-secondary"
                                onClick={() => handleManageBarcodes(product)}
                              >
                                <BarChart2 className="w-4 h-4" />
                              </button>
                              <button
                                className="btn btn-sm btn-error"
                                onClick={() => handleDeleteProduct(product.id)}
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      )}



      {/* Reporting Tab Content */}
      {activeTab === 'reporting' && (
        <div className="space-y-6">
          <div className="card bg-base-100 shadow-xl">
            <div className="card-body">
              <h2 className="card-title">Accuracy Reports</h2>
              
              {/* Date Range Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">Start Date</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">End Date</span>
                  </label>
                  <input
                    type="date"
                    className="input input-bordered"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                  />
                </div>
                
                <div className="form-control">
                  <label className="label">
                    <span className="label-text">&nbsp;</span>
                  </label>
                  <button
                    className="btn btn-primary"
                    onClick={fetchReportingData}
                    disabled={loadingReports || !reportStartDate || !reportEndDate}
                  >
                    {loadingReports ? (
                      <>
                        <span className="loading loading-spinner loading-sm"></span>
                        Loading...
                      </>
                    ) : (
                      'Generate Report'
                    )}
                  </button>
                </div>
              </div>

              {/* Summary Metrics */}
              {reportingData && (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="stat bg-purple-600 text-white rounded-lg">
                      <div className="stat-title text-white/70">Total Items Counted</div>
                      <div className="stat-value">{reportingData.totalItems.toLocaleString()}</div>
                    </div>
                    
                    <div className="stat bg-purple-600 text-white rounded-lg">
                      <div className="stat-title text-white/70">Correct Items</div>
                      <div className="stat-value">{reportingData.correctItems.toLocaleString()}</div>
                      <div className="stat-desc text-white/70">
                        {reportingData.correctPercentage.toFixed(1)}% accuracy
                      </div>
                    </div>
                    
                    <div className="stat bg-purple-600 text-white rounded-lg">
                      <div className="stat-title text-white/70">Correct SKU/Location %</div>
                      <div className="stat-value">{reportingData.correctPercentage.toFixed(1)}%</div>
                      <div className="stat-desc text-white/70">
                        Exact quantity matches
                      </div>
                    </div>
                    
                    <div className="stat bg-purple-600 text-white rounded-lg">
                      <div className="stat-title text-white/70">Average Discrepancy</div>
                      <div className="stat-value">{reportingData.averageDiscrepancy.toFixed(1)}%</div>
                      <div className="stat-desc text-white/70">
                        Mean absolute error
                      </div>
                    </div>
                  </div>

                  {/* Daily Trend Table */}
                  {reportingData.dailyData.length > 0 && (
                    <div>
                      <h3 className="text-xl font-semibold mb-4">Daily Trends</h3>
                      <div className="overflow-x-auto">
                        <table className="table table-zebra w-full">
                          <thead>
                            <tr>
                              <th>Date</th>
                              <th>Items Counted</th>
                              <th>Correct Items</th>
                              <th>Accuracy %</th>
                              <th>Avg Discrepancy %</th>
                            </tr>
                          </thead>
                          <tbody>
                            {reportingData.dailyData.map((day: any) => (
                              <tr key={day.date}>
                                <td>{new Date(day.date).toLocaleDateString()}</td>
                                <td>{day.totalItems.toLocaleString()}</td>
                                <td>{day.correctItems.toLocaleString()}</td>
                                <td>
                                  <span className={`badge ${
                                    day.correctPercentage >= 90 ? 'badge-success' : 
                                    day.correctPercentage >= 70 ? 'badge-warning' : 'badge-error'
                                  }`}>
                                    {day.correctPercentage.toFixed(1)}%
                                  </span>
                                </td>
                                <td>
                                  <span className={`badge ${
                                    day.averageDiscrepancy <= 10 ? 'badge-success' : 
                                    day.averageDiscrepancy <= 25 ? 'badge-warning' : 'badge-error'
                                  }`}>
                                    {day.averageDiscrepancy.toFixed(1)}%
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Explanation */}
                  <div className="alert alert-info">
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" className="stroke-current shrink-0 w-6 h-6">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                    </svg>
                    <div>
                      <div className="font-bold">Report Explanation:</div>
                      <div className="text-sm mt-1">
                        <strong>Correct SKU/Location %:</strong> Percentage of items where the actual quantity exactly matched the expected quantity.<br/>
                        <strong>Average Discrepancy:</strong> Mean absolute percentage difference between expected and actual quantities. Capped at 100% to avoid extreme outliers.
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {reportingData && reportingData.totalItems === 0 && (
                <div className="alert alert-warning">
                  <svg xmlns="http://www.w3.org/2000/svg" className="stroke-current shrink-0 h-6 w-6" fill="none" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L3.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                  </svg>
                  <span>No completed count items found for the selected date range.</span>
                </div>
              )}

              {!reportingData && !loadingReports && (
                <div className="text-center py-8">
                  <div className="text-base-content/70">
                    Select a date range and click "Generate Report" to view accuracy metrics.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}



      {/* Product Add/Edit Modal */}
      {showProductModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              {editingProduct ? 'Edit Product' : 'Add New Product'}
            </h3>
            
            <div className="space-y-4">
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Item Reference</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., FDGLE-01-10-12-90006"
                  className="input input-bordered w-full"
                  value={productForm.item_ref}
                  onChange={(e) => setProductForm({...productForm, item_ref: e.target.value})}
                  disabled={!!editingProduct} // Can't change item_ref when editing
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Product Name</span>
                </label>
                <input
                  type="text"
                  placeholder="e.g., Tadalafil 20mg"
                  className="input input-bordered w-full"
                  value={productForm.name}
                  onChange={(e) => setProductForm({...productForm, name: e.target.value})}
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Description (Optional)</span>
                </label>
                <textarea
                  className="textarea textarea-bordered"
                  placeholder="Additional product details..."
                  value={productForm.description}
                  onChange={(e) => setProductForm({...productForm, description: e.target.value})}
                />
              </div>
              
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Unit</span>
                </label>
                <select
                  className="select select-bordered w-full"
                  value={productForm.unit}
                  onChange={(e) => setProductForm({...productForm, unit: e.target.value})}
                >
                  <option value="EA">Each (EA)</option>
                  <option value="BOX">Box</option>
                  <option value="PACK">Pack</option>
                  <option value="KG">Kilogram</option>
                  <option value="L">Liter</option>
                  <option value="M">Meter</option>
                </select>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                className="btn btn-ghost"
                onClick={() => setShowProductModal(false)}
              >
                Cancel
              </button>
              <button 
                className="btn btn-primary"
                onClick={handleSaveProduct}
                disabled={!productForm.item_ref || !productForm.name}
              >
                {editingProduct ? 'Update Product' : 'Add Product'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Management Modal */}
      {showBarcodeModal && (
        <div className="modal modal-open">
          <div className="modal-box">
            <h3 className="font-bold text-lg mb-4">
              Manage Barcodes for {editingProduct?.name}
            </h3>
            
            <div className="space-y-4">
              {/* Add new barcode */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Add New Barcode</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter barcode"
                    className="input input-bordered flex-1"
                    value={newBarcode}
                    onChange={(e) => setNewBarcode(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddBarcode();
                      }
                    }}
                  />
                  <button 
                    className="btn btn-primary"
                    onClick={handleAddBarcode}
                    disabled={!newBarcode.trim()}
                  >
                    Add
                  </button>
                </div>
              </div>
              
              {/* Existing barcodes */}
              <div className="form-control">
                <label className="label">
                  <span className="label-text">Existing Barcodes</span>
                </label>
                <div className="space-y-2">
                  {currentProductBarcodes.length === 0 ? (
                    <div className="text-center text-base-content/70 p-4 bg-base-200 rounded">
                      No barcodes found for this product
                    </div>
                  ) : (
                    currentProductBarcodes.map((barcode) => (
                      <div key={barcode.id} className="flex items-center justify-between p-3 bg-base-200 rounded">
                        <span className="font-mono">{barcode.barcode}</span>
                        <button
                          className="btn btn-sm btn-error"
                          onClick={() => handleDeleteBarcode(barcode.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
            
            <div className="modal-action">
              <button 
                className="btn btn-ghost"
                onClick={() => setShowBarcodeModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Session Details Modal */}
      {showSessionDetailsModal && selectedSession && (
        <div className="modal modal-open">
          <div className="modal-box max-w-4xl">
            <h3 className="font-bold text-lg mb-4">
              Session Details: {selectedSession.name}
            </h3>
            
            {/* Session Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="stat bg-base-200 rounded-lg">
                <div className="stat-title">Site</div>
                <div className="stat-value text-sm text-purple-600">{selectedSession.site}</div>
              </div>
              <div className="stat bg-base-200 rounded-lg">
                <div className="stat-title">Status</div>
                <div className="stat-value text-sm">
                  <span className={`badge ${
                    selectedSession.status === 'completed' ? 'badge-success' :
                    selectedSession.status === 'reopened' ? 'badge-info' :
                    selectedSession.status === 'active' ? 'badge-warning' :
                    'badge-ghost'
                  }`}>
                    {selectedSession.status}
                  </span>
                </div>
              </div>
              <div className="stat bg-base-200 rounded-lg">
                <div className="stat-title">Progress</div>
                <div className="stat-value text-sm text-purple-600">
                  {selectedSession.completed_count} / {selectedSession.items_count}
                </div>
              </div>
            </div>

            {/* Session Items */}
            <div className="mb-4">
              <h4 className="font-semibold text-lg mb-2">Count Items</h4>
              {loadingSessionItems ? (
                <div className="flex items-center justify-center p-8">
                  <div className="loading loading-spinner loading-lg"></div>
                </div>
              ) : (
                <div className="overflow-x-auto max-h-96">
                  <table className="table table-compact w-full">
                    <thead>
                      <tr>
                        <th>Line</th>
                        <th>Location</th>
                        <th>Product</th>
                        <th>Item Ref</th>
                        <th>Expected</th>
                        <th>Actual</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {sessionItems.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-center text-base-content/70">
                            No items found in this session
                          </td>
                        </tr>
                      ) : (
                        sessionItems.map((item) => (
                          <tr key={item.id}>
                            <td>
                              <span className="font-mono text-xs text-primary">
                                {item.csv_line_number ? item.csv_line_number - 25 : 'N/A'}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-2">
                                <MapPin className="w-4 h-4 text-primary" />
                                <span className="font-mono text-sm">{item.location}</span>
                              </div>
                            </td>
                            <td>
                              <div>
                                <div className="font-medium">{item.products.name}</div>
                                {item.lot && (
                                  <div className="text-xs text-base-content/70">
                                    Lot: {item.lot}
                                  </div>
                                )}
                              </div>
                            </td>
                            <td>
                              <span className="font-mono text-sm">{item.products.item_ref}</span>
                            </td>
                            <td>
                              <span className="font-semibold">
                                {item.expected_quantity} {item.products.unit}
                              </span>
                            </td>
                            <td>
                              {item.actual_quantity !== null ? (
                                <span className="font-semibold text-success">
                                  {item.actual_quantity} {item.products.unit}
                                </span>
                              ) : (
                                <span className="text-base-content/50">-</span>
                              )}
                            </td>
                            <td>
                              <span className={`badge badge-sm ${
                                item.status === 'completed' ? 'badge-success' :
                                item.status === 'in_progress' ? 'badge-warning' :
                                'badge-ghost'
                              }`}>
                                {item.status}
                              </span>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {selectedSession.description && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Description</h4>
                <p className="text-base-content/70 bg-base-200 p-3 rounded">
                  {selectedSession.description}
                </p>
              </div>
            )}
            
            {sessionItems.length > 0 && sessionItems[0].original_worksheet_id && (
              <div className="mb-4">
                <h4 className="font-semibold mb-2">Original Worksheet ID</h4>
                <p className="text-base-content/70 bg-base-200 p-3 rounded font-mono">
                  {sessionItems[0].original_worksheet_id}
                </p>
              </div>
            )}
            
            <div className="modal-action">
              <button 
                className="btn btn-ghost"
                onClick={() => setShowSessionDetailsModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard; 